import { useState, useEffect, useMemo, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import {
  ArrowsDownUp, Funnel, Lightning, Star, Trash, ArrowSquareOut,
  RocketLaunch, MagnifyingGlass, X, CaretLeft, CaretRight, SpinnerGap
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function ScoreBadge({ score, scored }) {
  if (!scored) return <span className="font-mono text-xs text-zinc-700">--</span>;
  const color = score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";
  return <span className="font-mono text-sm font-semibold" style={{ color }}>{score}</span>;
}

export default function JobBoard() {
  const {
    jobs, fetchJobs, setSelectedJob, updateJobStatus, scoreJob, deleteJob,
    loading, totalJobsCount, currentPage, totalPages, scoreAllUnscored,
    scoringProgress, fetchScoringStatus
  } = useApp();

  const [sortBy, setSortBy] = useState("score");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [filterScore, setFilterScore] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [initialized, setInitialized] = useState(false);

  const loadPage = useCallback((page, filters = {}) => {
    const query = {};
    if (filters.status && filters.status !== "all") query.status = filters.status;
    if (filters.source && filters.source !== "all") query.source = filters.source;
    if (filters.sortBy) query.sort_by = filters.sortBy;
    if (filters.minScore) query.min_score = filters.minScore;
    fetchJobs(query, page);
  }, [fetchJobs]);

  useEffect(() => {
    if (!initialized) {
      loadPage(1, { sortBy });
      setInitialized(true);
    }
  }, [initialized, loadPage, sortBy]);

  // Re-fetch when server-side filters change
  const applyServerFilters = useCallback(() => {
    const filters = { sortBy };
    if (filterStatus !== "all") filters.status = filterStatus;
    if (filterSource !== "all") filters.source = filterSource;
    if (filterScore === "90+") filters.minScore = "90";
    else if (filterScore === "75+") filters.minScore = "75";
    else if (filterScore === "60+") filters.minScore = "60";
    loadPage(1, filters);
  }, [filterStatus, filterSource, filterScore, sortBy, loadPage]);

  useEffect(() => {
    if (initialized) applyServerFilters();
  }, [filterStatus, filterSource, filterScore, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get unique sources for filter (from current page)
  const sources = useMemo(() => {
    const s = new Set((jobs || []).map(j => j.source).filter(Boolean));
    return Array.from(s).sort();
  }, [jobs]);

  // Client-side text filter only (server handles the rest)
  const filtered = useMemo(() => {
    if (!searchText) return jobs || [];
    const q = searchText.toLowerCase();
    return (jobs || []).filter(j => {
      return (j.title || "").toLowerCase().includes(q)
        || (j.company || "").toLowerCase().includes(q)
        || (j.location || "").toLowerCase().includes(q);
    });
  }, [jobs, searchText]);

  const activeFilters = [filterStatus, filterSource, filterScore, searchText].filter(f => f && f !== "all").length;

  const clearFilters = () => {
    setFilterStatus("all"); setFilterSource("all"); setFilterScore("all"); setSearchText("");
  };

  const handleQuickApply = async (e, job) => {
    e.stopPropagation();
    if (job.job_url) window.open(job.job_url, '_blank');
    if (job.status !== 'applied') {
      await updateJobStatus(job.id, 'applied');
      toast.success(`Applied to ${job.title}! Moved to pipeline.`);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    const filters = { sortBy };
    if (filterStatus !== "all") filters.status = filterStatus;
    if (filterSource !== "all") filters.source = filterSource;
    if (filterScore === "90+") filters.minScore = "90";
    else if (filterScore === "75+") filters.minScore = "75";
    else if (filterScore === "60+") filters.minScore = "60";
    loadPage(newPage, filters);
  };

  const handleScoreAll = async () => {
    try {
      const result = await scoreAllUnscored();
      if (result.unscored === 0) {
        toast.success("All jobs are already scored!");
      } else {
        toast.success(`Scoring ${result.unscored} jobs in background...`);
      }
    } catch (e) {
      toast.error("Failed to trigger scoring");
    }
  };

  const isLoading = loading.fetchJobs;

  return (
    <div className="p-6 lg:p-8" data-testid="job-board">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 animate-in">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white">Results</h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="job-count-label">
            {searchText ? `${filtered.length} matching` : `Page ${currentPage} of ${totalPages}`} — {totalJobsCount} total jobs
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="score-all-btn"
            onClick={handleScoreAll}
            disabled={loading.scoreAll}
            className="bg-amber-500/15 border border-amber-500/25 text-amber-400 hover:bg-amber-500/25 rounded-md text-xs h-9 px-4 font-medium"
          >
            <Lightning size={14} weight="fill" className="mr-1.5" />
            {loading.scoreAll ? "Triggering..." : "Score All Unscored"}
          </Button>
        </div>
      </div>

      {/* Scoring Progress Banner */}
      {scoringProgress && scoringProgress.unscored > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 mb-4 rounded-lg border border-amber-500/20 bg-amber-500/5 animate-in" data-testid="scoring-progress-banner">
          <SpinnerGap size={16} className="text-amber-500 animate-spin" />
          <span className="text-xs text-amber-400">
            Scoring in progress: {scoringProgress.scored}/{scoringProgress.total} ({scoringProgress.percent}%)
          </span>
          <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${scoringProgress.percent}%` }} />
          </div>
          <span className="text-[10px] text-zinc-600">{scoringProgress.unscored} remaining</span>
        </div>
      )}

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 animate-in" data-testid="filters-bar">
        {/* Text Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Filter by title, company, location..."
            data-testid="search-filter-input"
            className="w-full bg-[#121212] border border-white/10 rounded-md pl-8 pr-3 py-2 text-xs text-white placeholder:text-zinc-600 focus:border-amber-500/50 outline-none"
          />
          {searchText && (
            <button onClick={() => setSearchText("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white">
              <X size={12} />
            </button>
          )}
        </div>

        {/* Score Filter */}
        <Select value={filterScore} onValueChange={setFilterScore}>
          <SelectTrigger className="bg-[#121212] border-white/10 rounded-md h-9 text-xs w-28" data-testid="filter-score-select">
            <Lightning size={13} className="mr-1 text-amber-500" /><SelectValue placeholder="Score" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md">
            <SelectItem value="all" className="text-xs">All Scores</SelectItem>
            <SelectItem value="90+" className="text-xs">90%+ Strong</SelectItem>
            <SelectItem value="75+" className="text-xs">75%+ Good</SelectItem>
            <SelectItem value="60+" className="text-xs">60%+ Match</SelectItem>
          </SelectContent>
        </Select>

        {/* Source Filter */}
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="bg-[#121212] border-white/10 rounded-md h-9 text-xs w-28" data-testid="filter-source-select">
            <Funnel size={13} className="mr-1" /><SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md">
            <SelectItem value="all" className="text-xs">All Sources</SelectItem>
            {sources.map(s => (
              <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="bg-[#121212] border-white/10 rounded-md h-9 text-xs w-28" data-testid="filter-status-select">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md">
            <SelectItem value="all" className="text-xs">All Status</SelectItem>
            <SelectItem value="new" className="text-xs">New</SelectItem>
            <SelectItem value="saved" className="text-xs">Saved</SelectItem>
            <SelectItem value="applied" className="text-xs">Applied</SelectItem>
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="bg-[#121212] border-white/10 rounded-md h-9 text-xs w-28" data-testid="sort-select">
            <ArrowsDownUp size={13} className="mr-1" /><SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/10 rounded-md">
            <SelectItem value="score" className="text-xs">By Score</SelectItem>
            <SelectItem value="date" className="text-xs">By Date</SelectItem>
            <SelectItem value="company" className="text-xs">By Company</SelectItem>
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {activeFilters > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-zinc-500 hover:text-white h-9" data-testid="clear-filters-btn">
            <X size={12} className="mr-1" /> Clear ({activeFilters})
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="jp-card animate-in delay-1">
        <div className="grid grid-cols-[60px_1fr_140px_100px_90px_110px] gap-3 px-5 py-3 border-b border-white/5 bg-white/[0.02]">
          <span className="jp-label">SCORE</span>
          <span className="jp-label">POSITION</span>
          <span className="jp-label">COMPANY</span>
          <span className="jp-label">SOURCE</span>
          <span className="jp-label">STATUS</span>
          <span className="jp-label text-right">ACTIONS</span>
        </div>

        {isLoading ? (
          <div className="p-12 text-center" data-testid="jobs-loading">
            <SpinnerGap size={28} className="mx-auto text-amber-500/60 animate-spin mb-3" />
            <div className="text-sm text-zinc-500">Loading jobs...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-600 text-sm" data-testid="jobs-empty">
            {totalJobsCount === 0 ? "No jobs found. Run a search first." : "No jobs match your filters."}
          </div>
        ) : (
          filtered.map(job => (
            <div key={job.id} data-testid={`job-row-${job.id}`}
              className="grid grid-cols-[60px_1fr_140px_100px_90px_110px] gap-3 items-center px-5 py-3.5 border-b border-white/5 cursor-pointer hover:bg-white/[0.02] transition-colors"
              onClick={() => setSelectedJob(job)}>
              <ScoreBadge score={job.match_score} scored={job.scored} />
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{job.title}</div>
                <div className="text-[11px] text-zinc-600 truncate">{job.location}</div>
              </div>
              <div className="text-xs text-zinc-400 truncate">{job.company}</div>
              <div className="flex items-center gap-2">
                <div className={`source-dot source-${(job.source || '').toLowerCase()}`} />
                <span className="text-[10px] text-zinc-600 uppercase">{job.source}</span>
              </div>
              <span className={`jp-badge ${
                job.status === "saved" ? "jp-badge-zinc" : job.status === "applied" ? "jp-badge-sky" :
                job.status === "interview" ? "jp-badge-amber" : job.status === "offer" ? "jp-badge-emerald" :
                job.status === "rejected" ? "jp-badge-red" : "jp-badge-zinc"}`}>{job.status}</span>
              <div className="flex justify-end gap-1">
                {!job.scored && (
                  <Button size="sm" variant="ghost" data-testid={`score-btn-${job.id}`}
                    className="h-7 w-7 p-0 text-amber-500 hover:bg-amber-500/10 rounded-md"
                    onClick={(e) => { e.stopPropagation(); scoreJob(job.id); }}
                    disabled={loading[`score-${job.id}`]}>
                    <Lightning size={14} weight="fill" />
                  </Button>
                )}
                {job.job_url && (
                  <Button size="sm" variant="ghost" data-testid={`apply-btn-${job.id}`}
                    className="h-7 w-7 p-0 text-zinc-600 hover:bg-amber-500/10 hover:text-amber-500 rounded-md"
                    title="Apply Now"
                    onClick={(e) => handleQuickApply(e, job)}>
                    <RocketLaunch size={14} />
                  </Button>
                )}
                <Button size="sm" variant="ghost" data-testid={`save-btn-${job.id}`}
                  className="h-7 w-7 p-0 text-zinc-600 hover:bg-amber-500/10 hover:text-amber-500 rounded-md"
                  onClick={(e) => { e.stopPropagation(); updateJobStatus(job.id, "saved"); toast.success("Saved"); }}>
                  <Star size={14} />
                </Button>
                <Button size="sm" variant="ghost" data-testid={`delete-btn-${job.id}`}
                  className="h-7 w-7 p-0 text-zinc-700 hover:bg-red-500/10 hover:text-red-400 rounded-md"
                  onClick={(e) => { e.stopPropagation(); deleteJob(job.id); }}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && !isLoading && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/5" data-testid="pagination-controls">
            <span className="text-xs text-zinc-600">
              Showing {(currentPage - 1) * 50 + 1}–{Math.min(currentPage * 50, totalJobsCount)} of {totalJobsCount}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost" size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="h-8 w-8 p-0 text-zinc-500 hover:text-white disabled:opacity-30"
                data-testid="pagination-prev"
              >
                <CaretLeft size={16} />
              </Button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (currentPage <= 4) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    data-testid={`page-btn-${pageNum}`}
                    className={`h-8 w-8 rounded-md text-xs font-mono transition-colors ${
                      pageNum === currentPage
                        ? 'bg-amber-500 text-black font-semibold'
                        : 'text-zinc-500 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <Button
                variant="ghost" size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="h-8 w-8 p-0 text-zinc-500 hover:text-white disabled:opacity-30"
                data-testid="pagination-next"
              >
                <CaretRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
